/**
 * RBAC-4.3 · Remove access now, and hand over sole administration (FR-32, FR-33a, UX-DR16).
 *
 * Pure, no DOM, no Supabase. The RPCs are `20261102a2_rbac_4_3_remove_access_and_handover.sql`,
 * called through `services/personAccessService.ts`; the screens are `RemoveAccessDialog`,
 * `HandoverDialog` and `RemovedPeopleSection` in `components/settings/components/users/`.
 *
 * ⚠️ THE DATABASE DECIDES; THIS ONLY SAYS SO FIRST. Self-removal, the last administrator, privileged
 * holders, RBAC-1.5 on restore, and every handover rule are refused server-side. Nothing here enforces.
 *
 * ⚠️ `REMOVE_ACCESS_EFFECT` IS LOAD-BEARING COPY (AC 1, "the affordance says so"). It is true because
 * of T02/T03 of the verify suite, and the test pins it so a tidy-up has to be a decision.
 */

import { duplicateRoleErrorMessage, type RpcErrorLike } from './roleDuplication';
import { youWillLoseSentence } from './rolePermissionDiff';

export const REMOVE_PERSON_ACCESS_RPC = 'remove_person_access';
export const RESTORE_PERSON_ACCESS_RPC = 'restore_person_access';
export const REMOVED_PEOPLE_RPC = 'removed_people';
export const HANDOVER_OPTIONS_RPC = 'administration_handover_options';
export const HANDOVER_RPC = 'handover_administration';

export const REMOVE_ACCESS_EFFECT =
  'It takes effect on their next request, not after a delay. They are signed out on every device, and anything they have open stops working.';

export interface RemovedPerson {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  roleName: string | null;
  changedAt: string | null;
}

export type HandoverUnavailable = 'superadmin' | 'other_account' | 'privileged' | 'not_by_role';
const REASONS: readonly HandoverUnavailable[] = ['superadmin', 'other_account', 'privileged', 'not_by_role'];

export interface HandoverCandidate {
  userId: string;
  firstName: string;
  lastName: string;
  roleId: number | null;
  roleName: string | null;
}

export interface HandoverRole {
  id: number;
  name: string;
  isGlobal: boolean;
  /** RBAC-5.1 FR-2e: permissions the caller personally stops holding on this role (the database's answer). */
  youLose: string[];
}

export interface HandoverOptions {
  canHandOver: boolean;
  cannotReason: HandoverUnavailable | null;
  myRoleId: number | null;
  myRoleName: string | null;
  /** Administrators by role other than the caller. 0 means the caller is the last one. */
  otherAdministrators: number;
  candidates: HandoverCandidate[];
  myRoles: HandoverRole[];
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const strOrNull = (v: unknown) => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
const rows = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object') : [];

export function parseRemovedPeople(raw: unknown): RemovedPerson[] {
  return rows(raw)
    .filter((e) => typeof e.user_id === 'string')
    .map((e) => ({
      userId: e.user_id as string,
      firstName: str(e.first_name),
      lastName: str(e.last_name),
      email: str(e.email),
      roleName: strOrNull(e.role_name),
      changedAt: strOrNull(e.changed_at),
    }));
}

export function parseHandoverOptions(raw: unknown): HandoverOptions | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.can_hand_over !== 'boolean') return null;
  return {
    canHandOver: r.can_hand_over,
    cannotReason: REASONS.includes(r.cannot_reason as HandoverUnavailable) ? (r.cannot_reason as HandoverUnavailable) : null,
    myRoleId: num(r.my_role_id),
    myRoleName: strOrNull(r.my_role_name),
    otherAdministrators: num(r.other_administrators) ?? 0,
    candidates: rows(r.candidates)
      .filter((e) => typeof e.user_id === 'string')
      .map((e) => ({
        userId: e.user_id as string,
        firstName: str(e.first_name),
        lastName: str(e.last_name),
        roleId: num(e.role_id),
        roleName: strOrNull(e.role_name),
      })),
    myRoles: rows(r.my_roles)
      .map((e) => ({
        id: num(e.id),
        name: str(e.name),
        isGlobal: e.is_global === true,
        youLose: Array.isArray(e.you_lose) ? e.you_lose.filter((k): k is string => typeof k === 'string') : [],
      }))
      .filter((e): e is HandoverRole => e.id !== null && e.name !== ''),
  };
}

const first = (firstName: string) => firstName.trim() || 'this person';
const fullName = (p: { firstName: string; lastName: string }) =>
  [p.firstName.trim(), p.lastName.trim()].filter(Boolean).join(' ') || 'this person';

export function removeAccessCopy(person: { firstName: string }) {
  return {
    title: `Remove ${first(person.firstName)}'s access now?`,
    effect: REMOVE_ACCESS_EFFECT,
    kept: 'Their records, role and history stay. You can restore their access from Users → Access removed.',
    confirmLabel: 'Remove access now',
  };
}

export function handoverPreview(input: {
  successor: { firstName: string; lastName: string };
  adminRoleName: string;
  myRoleName: string;
  /** FR-2e: what the caller personally loses on that role, from administration_handover_options. */
  youLose: readonly string[];
  otherAdministrators: number;
}) {
  const who = input.successor.firstName.trim() || fullName(input.successor);
  return {
    title: `Hand administration over to ${fullName(input.successor)}?`,
    lines: [
      `${who} becomes ${input.adminRoleName} and can administer this account.`,
      input.youLose.length > 0
        ? `You become ${input.myRoleName}. ${youWillLoseSentence(input.youLose)} Only an administrator can give these back.`
        : `You become ${input.myRoleName} and keep everything you can do now.`,
      'Both changes happen together, or neither does.',
    ],
    soleNote: input.otherAdministrators === 0 ? `${who} will be the only administrator of this account.` : null,
    confirmLabel: `Hand over to ${who}`,
  };
}

/** Your own record: Change role is refused (F-136), and "ask another administrator" may name nobody. */
export const SELF_ROLE_SENTENCE =
  "You can't change your own role. To step down or before you leave, hand administration over to someone.";

/** Shown on your own record when nobody else administers (FR-33a's whole reason to exist). */
export function soleAdministratorSentence(otherAdministrators: number): string | null {
  return otherAdministrators === 0
    ? 'You are the only administrator of this account. If you are leaving, hand administration over first: nobody else here can do it for you.'
    : null;
}

export function handoverUnavailableSentence(reason: HandoverUnavailable | null): string | null {
  switch (reason) {
    case 'privileged':
      return 'You hold Agency Admin, which only a Joules superadmin can hand over. Ask them to move it.';
    case 'superadmin':
      return 'Superadmins do not hand over administration. Change the roles directly.';
    case 'other_account':
      return 'Hand administration over from your own account.';
    case 'not_by_role':
      return 'Your administration does not come from your role, so there is nothing here to hand over.';
    default:
      return null;
  }
}

export function handoverArgs(input: { successorId: string; successorRoleId: number | null; myRoleId: number; myCurrentRoleId: number | null }) {
  return {
    p_successor_id: input.successorId,
    p_successor_expected_id: input.successorRoleId,
    p_my_role_id: input.myRoleId,
    p_my_expected_id: input.myCurrentRoleId,
  };
}

const withFallback = (fallback: string) => (err: RpcErrorLike | null | undefined) =>
  err?.message?.trim() ? duplicateRoleErrorMessage(err) : fallback;

export const removeAccessErrorMessage = withFallback('Their access was not removed. Check your connection and try again.');
export const restoreAccessErrorMessage = withFallback('Their access was not restored. Check your connection and try again.');
export const handoverErrorMessage = withFallback('Nothing was handed over. Check your connection and try again.');

export function isStaleHandover(err: RpcErrorLike | null | undefined): boolean {
  return err?.code === '22023' && /changed since you opened/.test(err.message ?? '');
}
